import styled from '@emotion/styled';
import { useState, useEffect, useRef, useMemo } from 'react';
import { useRouter } from 'next/router';
import ScheduleCalendar from '@base/ScheduleCalendar/ScheduleCalendar';
import ChevronLeftPlanner from '@icon/Planner/ChevronLeftPlanner';
import ChevronRightPlanner from '@icon/Planner/ChevronRightPlanner';
import { getCalendarDateV2 } from '@stores/selectors/planner';
import { Spacings, Fonts } from '@styles/index';
import { segment } from '@actions/analytics';
import {
  PLANNER_CALENDAR_DATE_SELECTED,
  PLANNER_PAGE
} from '@constants/segment';
import useGoalEventProps from '@hooks/useGoalEventProps';
import useCalendarInfo from '@api/hooks/planner/useCalendarInfo';
import { useCalendarStoreV2 } from '@stores/planner/calendarStore';
import {
  getDateKey,
  getStartOfTheDayInMs,
  getTodayDate
} from './planner-utils/date-time-helper';

const StyledCalendar = styled(ScheduleCalendar)`
  pointer-events: auto;

  &.react-calendar {
    position: static;
    width: 384px;
  }
  .react-calendar__month-view__weekdays__weekday {
    color: var(--color-text-secondary);
    width: 24px;
    height: 24px;
    flex-basis: none;
    max-width: 24px !important;
  }
  .react-calendar__month-view__weekdays {
    display: grid !important;
    grid-template-columns: repeat(7, 1fr);
    grid-row-gap: 20px;
    grid-column-gap: 18px;
    justify-items: center;
    align-items: center;
    align-content: center;
    justify-content: center;
  }
  .react-calendar__month-view__days {
    display: grid !important;
    grid-template-columns: repeat(7, 1fr);
    grid-row-gap: 20px;
    grid-column-gap: 18px;
    justify-items: center;
    align-items: center;
    align-content: center;
    justify-content: center;
    margin-top: 20px;
  }
  [type='button'].react-calendar__tile {
    font-weight: ${Fonts.SEMIBOLD};
    font-size: ${Fonts.P1};
    height: 32px;
    width: 100%;
    margin: 0 !important;
  }
  [type='button'].react-calendar__tile.react-calendar__tile--now {
    color: var(--color-text-primary) !important;
    background-color: transparent;
    border: 1px solid var(--color-text-primary);
    border-radius: 50%;
  }
  [type='button'].react-calendar__tile.react-calendar__tile--now:hover {
    background-color: var(--color-divider);
    border: none;
  }
  [type='button'].react-calendar__navigation__arrow.react-calendar__navigation__prev-button,
  [type='button'].react-calendar__navigation__arrow.react-calendar__navigation__next-button {
    min-width: 28px;
    min-height: 28px;
    width: 28px;
    border-radius: 50%;
    display: flex;
    justify-content: center;
    align-items: center;
  }

  .react-calendar__navigation__prev-button:hover:enabled,
  .react-calendar__navigation__next-button:hover:enabled {
    background-color: var(--color-divider);
  }
  .react-calendar__tile--active {
    color: var(--color-text-tertiary) !important;
    background-color: var(--color-text-primary) !important;
  }
  .react-calendar__tile.react-calendar__tile--now.react-calendar__tile--active {
    color: var(--color-text-tertiary) !important;
  }
  .react-calendar__tile--active:hover {
    background-color: var(--color-primary-focus) !important;
  }
  .react-calendar__month-view__days__day--neighboringMonth {
    visibility: hidden;
  }

  .react-calendar__navigation {
    margin-bottom: ${Spacings.SPACING_6B};
  }
`;

const getMonthStart = (date) => {
  const newDate = new Date(date);
  return new Date(
    new Date(newDate.getFullYear(), newDate.getMonth(), 1).setHours(0, 0, 0, 0)
  );
};

const getYearMonthString = (date) => {
  if (date) {
    const targetDate = new Date(date);
    return `${targetDate.getFullYear()}-${targetDate.getMonth() + 1}`;
  }
  return '';
};

const DaysOfTheWeek = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];
const formatShortWeekday = (_, date) => DaysOfTheWeek[date.getDay()];

const isOutOfBounds = (
  selectedDate,
  leftBoundaryDateInMs,
  rightBoundaryDateInMs
) => {
  if (!selectedDate) return false;
  const selectedDateInMs = getStartOfTheDayInMs(selectedDate);
  return !(
    selectedDateInMs >= leftBoundaryDateInMs &&
    selectedDateInMs < rightBoundaryDateInMs
  );
};

const prevLabel = <ChevronLeftPlanner size="18px" />;
const nextLabel = <ChevronRightPlanner size="18px" />;
const prevLabelDisabled = (
  <ChevronLeftPlanner size="18px" color="var(--color-divider)" />
);
const nextLabelDisabled = (
  <ChevronRightPlanner size="18px" color="var(--color-divider)" />
);

const Calendar = ({ datesAndKeys, dateSelected }) => {
  const {
    query: { goalUID = '' }
  } = useRouter();

  const datePointer =
    useCalendarStoreV2(getCalendarDateV2(goalUID)) ||
    getTodayDate().date.setHours(0, 0, 0, 0);
  const datePointerDate = useMemo(() => new Date(datePointer), [datePointer]);
  const [currentYearMonthPointer, setCurrentYearMonthPointer] = useState(-1);
  const [startDate, setStartDate] = useState('');
  const [calendarDate, setCalendarDate] = useState(null);

  const { calendarData = {} } = useCalendarInfo(
    goalUID,
    calendarDate,
    getYearMonthString(calendarDate)
  );
  const currentMonthStartDateRef = useRef(getMonthStart(datePointer));
  const goalSegmentData = useGoalEventProps({ goalUID });
  const firstDateInDatesAndKey = new Date(datesAndKeys[0]?.key);
  const lastDateInDatesAndKey = new Date(
    datesAndKeys[datesAndKeys.length - 1]?.key
  );

  const OnDateSelected = (date) => {
    const dateKey = getDateKey(date);

    const daysDiff = Math.trunc(
      (datePointer - Date.now()) / (1000 * 60 * 60 * 24)
    );
    segment.track(PLANNER_CALENDAR_DATE_SELECTED, {
      ...goalSegmentData,
      last_primary_source: PLANNER_PAGE,
      last_primary_source_section: 'Planner',
      days_difference: daysDiff < 0 ? daysDiff + 1 : daysDiff
    });
    if (dateSelected) {
      dateSelected(dateKey);
    }
  };

  useEffect(() => {
    if (currentMonthStartDateRef.current)
      setStartDate(currentMonthStartDateRef.current.getMonth());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentMonthStartDateRef.current]);

  useEffect(() => {
    const date = new Date(datePointer);
    const yearMonth = getYearMonthString(date);
    if (datePointer && yearMonth !== currentYearMonthPointer) {
      setCurrentYearMonthPointer(yearMonth);
      currentMonthStartDateRef.current = getMonthStart(datePointer);
      if (!calendarData[yearMonth]) {
        setCalendarDate(datePointer);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [datePointer, goalUID]);

  const onMonthNavigationClick = ({ activeStartDate }) => {
    if (
      isOutOfBounds(
        activeStartDate,
        datesAndKeys[0].date,
        datesAndKeys[datesAndKeys.length - 1].date
      )
    ) {
      return;
    }
    const monthYear = getYearMonthString(activeStartDate);
    setCurrentYearMonthPointer(monthYear);
    currentMonthStartDateRef.current = getMonthStart(activeStartDate);
    if (!calendarData[monthYear]) {
      setCalendarDate(activeStartDate.getTime());
    }
  };

  const getNextLabel = () => {
    if (startDate === lastDateInDatesAndKey?.getMonth())
      return nextLabelDisabled;
    return nextLabel;
  };

  const getPrevLabel = () => {
    if (startDate === firstDateInDatesAndKey?.getMonth())
      return prevLabelDisabled;
    return prevLabel;
  };

  return (
    <StyledCalendar
      value={datePointerDate}
      activeStartDate={currentMonthStartDateRef.current}
      locale="en-US"
      calendarType="US"
      formatShortWeekday={formatShortWeekday}
      prevLabel={getPrevLabel()}
      nextLabel={getNextLabel()}
      minDetail="month"
      next2Label={null}
      prev2Label={null}
      onClickDay={OnDateSelected}
      onActiveStartDateChange={onMonthNavigationClick}
      minDate={firstDateInDatesAndKey}
      maxDate={lastDateInDatesAndKey}
    />
  );
};

export default Calendar;
